/**
 * Phase 3.26 — Fahrzeug-Export + Fahrzeug-Import.
 * Extracted from DealerSettings.jsx. Renders at the bottom of
 * Marktplatzeinstellungen in DashboardPage.jsx.
 */
import { useEffect, useState } from "react";
import { Download, Upload, Key, CheckCircle2, Trash2, RefreshCw, Play, FileDown, AlertTriangle, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { CollapsibleServerToggle, CollapsibleCard } from "./_shared";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

// Deutsche Labels für die Spalten-Zuordnung (kanonische Feldnamen).
const FEED_FIELD_LABELS = {
    external_id: "Externe ID", title: "Titel", category: "Kategorie", brand: "Marke",
    model: "Modell", year: "Baujahr", price: "Preis", mileage: "Kilometer",
    condition: "Zustand", color: "Farbe", fuel: "Kraftstoff", power_kw: "Leistung (kW)",
    power_hp: "Leistung (PS)", displacement: "Hubraum", transmission: "Getriebe",
    bike_type: "Fahrradtyp", frame_size: "Rahmengröße", location: "Standort",
    postal_code: "PLZ", images: "Bilder", description: "Beschreibung",
};

export function VehicleExportSection({ currentUser }) {
    void currentUser;
    const exportFile = async (kind) => {
        try {
            const ext = kind === "csv" ? "csv" : kind === "json" ? "json" : "xml";
            const { data } = await api.get(`/export/listings.${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `listings.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`${ext.toUpperCase()}-Export gestartet`);
        } catch (e) {
            toast.error("Export fehlgeschlagen");
        }
    };
    return (
        <CollapsibleCard icon={Download} title="Fahrzeug-Export" hint="Laden Sie alle Ihre Inserate als Datei herunter." testId="vehicle-export-section">
            <div className="grid grid-cols-3 gap-2">
                <Button onClick={() => exportFile("csv")} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="export-csv-btn">CSV</Button>
                <Button onClick={() => exportFile("json")} variant="outline" className="border-[#111827] font-bold rounded-sm" data-testid="export-json-btn">JSON</Button>
                <Button onClick={() => exportFile("xml")} variant="outline" className="border-[#111827] font-bold rounded-sm" data-testid="export-xml-btn">XML</Button>
            </div>
        </CollapsibleCard>
    );
}

export function VehicleImportSection({ currentUser }) {
    const { refresh } = useAuth();
    const [tokens, setTokens] = useState([]);
    const [newToken, setNewToken] = useState(null);
    const [tokenName, setTokenName] = useState("");
    const [preview, setPreview] = useState(null);
    const [importing, setImporting] = useState(false);
    const [autoImport, setAutoImport] = useState({
        enabled: !!currentUser?.import_server_enabled || !!currentUser?.import_server_url,
        url: currentUser?.import_server_url || "",
        source_type: currentUser?.import_server_type || "url",
        sync_mode: currentUser?.import_sync_mode || "upsert",
        interval_minutes: currentUser?.import_interval_minutes || 60,
        external_id_field: currentUser?.import_external_id_field || "external_id",
        sftp_host: currentUser?.import_sftp_host || "",
        sftp_port: currentUser?.import_sftp_port || 22,
        sftp_user: currentUser?.import_sftp_user || "",
        sftp_password: "",
        sftp_path: currentUser?.import_sftp_path || "",
    });
    const [savingAutoImport, setSavingAutoImport] = useState(false);
    const [feedTesting, setFeedTesting] = useState(false);
    const [feedRunning, setFeedRunning] = useState(false);
    const [feedTestResult, setFeedTestResult] = useState(null);
    const [feedRuns, setFeedRuns] = useState([]);
    const [lastRunSummary, setLastRunSummary] = useState(currentUser?.import_last_run_summary || null);
    const [columnMap, setColumnMap] = useState(currentUser?.import_column_map || {});
    const sampleUrl = `${process.env.REACT_APP_BACKEND_URL}/api/feed-import/sample.csv`;
    const [open, setOpen] = useState({ csv: false, autofeed: false, tokens: false });
    const [sectionOpen, setSectionOpen] = useState(false);
    const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));
    const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const loadTokens = () => api.get("/api-tokens").then((r) => setTokens(r.data)).catch(() => {});
    const loadFeedRuns = () => api.get("/dealer/feed-import/runs").then((r) => setFeedRuns(r.data)).catch(() => {});
    useEffect(() => { loadTokens(); loadFeedRuns(); }, []);

    const saveAutoImport = async () => {
        setSavingAutoImport(true);
        try {
            const payload = {
                import_server_enabled: autoImport.enabled,
                import_server_type: autoImport.source_type,
                import_sync_mode: autoImport.sync_mode,
                import_interval_minutes: Number(autoImport.interval_minutes) || 60,
                import_external_id_field: autoImport.external_id_field || "external_id",
                import_column_map: columnMap || {},
            };
            if (autoImport.source_type === "sftp") {
                payload.import_sftp_host = autoImport.sftp_host;
                payload.import_sftp_port = Number(autoImport.sftp_port) || 22;
                payload.import_sftp_user = autoImport.sftp_user;
                payload.import_sftp_path = autoImport.sftp_path;
                if (autoImport.sftp_password) payload.import_sftp_password = autoImport.sftp_password;
                payload.import_server_url = "";
            } else {
                payload.import_server_url = autoImport.enabled ? autoImport.url : "";
            }
            await api.patch("/users/me/dealer-profile", payload);
            await refresh();
            toast.success("Auto-Import gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSavingAutoImport(false);
        }
    };

    const testFeed = async () => {
        setFeedTesting(true);
        setFeedTestResult(null);
        try {
            const body = {
                source_type: autoImport.source_type,
                external_id_field: autoImport.external_id_field || "external_id",
                column_map: columnMap || {},
            };
            if (autoImport.source_type === "sftp") {
                Object.assign(body, {
                    sftp_host: autoImport.sftp_host, sftp_port: Number(autoImport.sftp_port) || 22,
                    sftp_user: autoImport.sftp_user, sftp_path: autoImport.sftp_path,
                });
                if (autoImport.sftp_password) body.sftp_password = autoImport.sftp_password;
            } else {
                body.url = autoImport.url;
            }
            const { data } = await api.post("/dealer/feed-import/test", body);
            setFeedTestResult(data);
            // Auto-Erkennung: beim ersten Test (noch kein Mapping gesetzt) die
            // vorgeschlagenen Zuordnungen automatisch übernehmen.
            const suggested = data.suggested_map || {};
            const noMapYet = !columnMap || Object.keys(columnMap).length === 0;
            if (noMapYet && Object.keys(suggested).length > 0) {
                setColumnMap(suggested);
                toast.success(`${Object.keys(suggested).length} Spalten automatisch zugeordnet — bitte erneut testen`);
            } else if (data.missing_required && data.missing_required.length > 0) {
                toast.warning(`Bitte ${data.missing_required.length} Pflichtspalte(n) zuordnen`);
            } else {
                toast.success(`Verbindung OK — ${data.total} Zeilen, ${data.with_external_id} mit ID`);
            }
        } catch (e) {
            setFeedTestResult({ error: e.response?.data?.detail || "Test fehlgeschlagen" });
            toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
        } finally {
            setFeedTesting(false);
        }
    };

    const runFeedNow = async () => {
        setFeedRunning(true);
        try {
            const { data } = await api.post("/dealer/feed-import/run");
            setLastRunSummary(data);
            await loadFeedRuns();
            if (data.status === "ok") {
                toast.success(`Sync fertig: ${data.created} neu, ${data.updated} aktualisiert${data.deactivated ? `, ${data.deactivated} deaktiviert` : ""}`);
            } else {
                toast.error(`Sync mit Fehlern (${data.error_count})`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync fehlgeschlagen");
        } finally {
            setFeedRunning(false);
        }
    };

    const previewImport = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/import/listings/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview({ file, ...data });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
            setPreview(null);
        }
    };

    const commitImport = async () => {
        if (!preview?.file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", preview.file);
            const { data } = await api.post("/import/listings/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${data.inserted_count} Inserate angelegt${data.errors.length ? ` (${data.errors.length} Fehler)` : ""}`);
            setPreview(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import fehlgeschlagen");
        } finally {
            setImporting(false);
        }
    };

    const createToken = async () => {
        if (!tokenName.trim()) return;
        try {
            const { data } = await api.post("/api-tokens", { name: tokenName.trim(), scope: "read" });
            setNewToken(data);
            setTokenName("");
            await loadTokens();
        } catch (e) {
            toast.error("Erstellung fehlgeschlagen");
        }
    };

    const revokeToken = async (id) => {
        try {
            await api.delete(`/api-tokens/${id}`);
            await loadTokens();
            toast.success("Token widerrufen");
        } catch {
            toast.error("Widerruf fehlgeschlagen");
        }
    };

    const SubHeader = ({ id, icon: Icon, title, hint }) => (
        <button
            type="button"
            onClick={() => toggle(id)}
            className="w-full flex items-center gap-3 px-5 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 text-left"
            data-testid={`vehicle-import-toggle-${id}`}
            aria-expanded={open[id]}
        >
            <Icon className="w-4 h-4 text-[#2563EB] shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#111827]">{title}</div>
                <div className="text-[11px] text-gray-500">{hint}</div>
            </div>
            <span className={`text-gray-400 text-lg transition-transform ${open[id] ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
        </button>
    );

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="vehicle-import-section">
            <button
                type="button"
                onClick={() => setSectionOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-200"
                data-testid="vehicle-import-section-toggle"
                aria-expanded={sectionOpen}
            >
                <Upload className="w-5 h-5 text-[#2563EB] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-lg">Fahrzeug-Import</h3>
                    <p className="text-xs text-gray-600">CSV-Upload, Auto-Import (CSV/SFTP) & API-Tokens.</p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${sectionOpen ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {sectionOpen && (<>
            <div className="px-6 py-4 border-b border-gray-200">
                <p className="text-xs text-gray-600">
                    CSV-Datei mit Inseraten hochladen. Pflichtspalten: <code className="bg-gray-100 px-1 rounded">title, category, brand, model, year, price</code>.
                    Kategorie: <code className="bg-gray-100 px-1 rounded">motorcycle</code> oder <code className="bg-gray-100 px-1 rounded">bicycle</code>.
                </p>
            </div>

            <SubHeader id="csv" icon={Upload} title="CSV-Upload (Manuell)" hint="Einmaliger Upload einer Inserats-CSV." />
            {open.csv && (
                <div className="p-5 border-b border-gray-200" data-testid="vehicle-import-csv-body">
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => previewImport(e.target.files?.[0])}
                        className="block text-sm w-full file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#FFCC00] file:font-bold file:cursor-pointer cursor-pointer"
                        data-testid="import-file-input"
                    />
                    {preview && (
                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-sm">
                            <div className="text-sm font-bold mb-2">Vorschau ({preview.total} Zeilen)</div>
                            <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                                {preview.valid_rows.slice(0, 10).map((r) => (
                                    <div key={r.row} className="flex gap-2"><CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5 shrink-0" /> Z{r.row}: <strong>{r.preview.brand}</strong> {r.preview.model} – {r.preview.price}€</div>
                                ))}
                                {preview.error_rows.slice(0, 5).map((r, i) => (
                                    <div key={`e${i}`} className="flex gap-2 text-red-700">⚠ Z{r.row}: {r.error}</div>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-3">
                                <Button onClick={commitImport} disabled={importing || preview.valid_rows.length === 0} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm flex-1" data-testid="import-commit-btn">
                                    {importing ? "Importiere…" : `${preview.valid_rows.length} importieren`}
                                </Button>
                                <Button onClick={() => setPreview(null)} variant="outline" className="rounded-sm">Abbrechen</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <SubHeader id="autofeed" icon={RefreshCw} title="Auto-Import (CSV-Feed)" hint="Pullt regelmäßig Ihre Lager-CSV/SFTP — passend für DMS-Anbindungen." />
            {open.autofeed && (
                <div className="p-5 border-b border-gray-200 space-y-4" data-testid="vehicle-import-autofeed-body">
                    <CollapsibleServerToggle
                        enabled={autoImport.enabled}
                        onToggle={(v) => setAutoImport({ ...autoImport, enabled: v })}
                        title="Auto-Import (CSV-Feed)"
                        hint="Automatischer, stündlicher Abgleich Ihrer Lager-CSV per URL oder SFTP."
                        testId="import-server"
                    >
                        {/* Quelle */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Quelle</Label>
                                <select
                                    value={autoImport.source_type}
                                    onChange={(e) => setAutoImport({ ...autoImport, source_type: e.target.value })}
                                    className="mt-1 w-full border border-gray-300 rounded-sm h-10 px-2 text-sm bg-white"
                                    data-testid="import-source-type"
                                >
                                    <option value="url">HTTP(S)-URL</option>
                                    <option value="sftp">SFTP-Server</option>
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Abgleich-Modus</Label>
                                <select
                                    value={autoImport.sync_mode}
                                    onChange={(e) => setAutoImport({ ...autoImport, sync_mode: e.target.value })}
                                    className="mt-1 w-full border border-gray-300 rounded-sm h-10 px-2 text-sm bg-white"
                                    data-testid="import-sync-mode"
                                >
                                    <option value="upsert">Upsert (neu + aktualisieren)</option>
                                    <option value="full">Voll-Sync (entfernte deaktivieren)</option>
                                </select>
                            </div>
                        </div>

                        {autoImport.source_type === "url" ? (
                            <div className="mt-3">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Feed-URL</Label>
                                <Input
                                    value={autoImport.url}
                                    onChange={(e) => setAutoImport({ ...autoImport, url: e.target.value })}
                                    placeholder="https://server/lager.csv"
                                    className="rounded-sm mt-1 font-mono-num text-sm"
                                    data-testid="import-url-input"
                                />
                            </div>
                        ) : (
                            <div className="mt-3 grid grid-cols-2 gap-3" data-testid="import-sftp-fields">
                                <div className="col-span-2 grid grid-cols-3 gap-3">
                                    <div className="col-span-2">
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">SFTP-Host</Label>
                                        <Input value={autoImport.sftp_host} onChange={(e) => setAutoImport({ ...autoImport, sftp_host: e.target.value })} placeholder="sftp.dms.de" className="rounded-sm mt-1 text-sm" data-testid="import-sftp-host" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Port</Label>
                                        <Input type="number" value={autoImport.sftp_port} onChange={(e) => setAutoImport({ ...autoImport, sftp_port: e.target.value })} className="rounded-sm mt-1 text-sm" data-testid="import-sftp-port" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Benutzer</Label>
                                    <Input value={autoImport.sftp_user} onChange={(e) => setAutoImport({ ...autoImport, sftp_user: e.target.value })} className="rounded-sm mt-1 text-sm" data-testid="import-sftp-user" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Passwort {currentUser?.import_sftp_host && <span className="text-gray-400 normal-case">(leer = unverändert)</span>}</Label>
                                    <Input type="password" value={autoImport.sftp_password} onChange={(e) => setAutoImport({ ...autoImport, sftp_password: e.target.value })} className="rounded-sm mt-1 text-sm" data-testid="import-sftp-password" />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Dateipfad</Label>
                                    <Input value={autoImport.sftp_path} onChange={(e) => setAutoImport({ ...autoImport, sftp_path: e.target.value })} placeholder="/export/lager.csv" className="rounded-sm mt-1 font-mono-num text-sm" data-testid="import-sftp-path" />
                                </div>
                            </div>
                        )}

                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Intervall (Minuten)</Label>
                                <Input type="number" min={15} max={1440} value={autoImport.interval_minutes} onChange={(e) => setAutoImport({ ...autoImport, interval_minutes: e.target.value })} className="rounded-sm mt-1 text-sm" data-testid="import-interval" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Spalte „externe ID"</Label>
                                <Input value={autoImport.external_id_field} onChange={(e) => setAutoImport({ ...autoImport, external_id_field: e.target.value })} placeholder="external_id" className="rounded-sm mt-1 font-mono-num text-sm" data-testid="import-extid-field" />
                            </div>
                        </div>
                    </CollapsibleServerToggle>

                    <div className="flex flex-wrap gap-2">
                        <Button onClick={saveAutoImport} disabled={savingAutoImport} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm uppercase tracking-wider flex-1 min-w-[140px]" data-testid="save-auto-import-btn">
                            {savingAutoImport ? "Speichert…" : "Speichern"}
                        </Button>
                        <Button onClick={testFeed} disabled={feedTesting} variant="outline" className="border-[#111827] font-bold rounded-sm" data-testid="feed-test-btn">
                            {feedTesting ? "Teste…" : "Verbindung testen"}
                        </Button>
                        <Button onClick={runFeedNow} disabled={feedRunning} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm" data-testid="feed-run-btn">
                            <Play className="w-4 h-4 mr-1" />{feedRunning ? "Synchronisiere…" : "Jetzt synchronisieren"}
                        </Button>
                    </div>

                    <a href={sampleUrl} className="inline-flex items-center gap-1 text-xs text-[#2563EB] hover:underline" data-testid="feed-sample-link">
                        <FileDown className="w-3 h-3" /> Beispiel-/Vorlagen-CSV herunterladen
                    </a>

                    {/* Test-Ergebnis */}
                    {feedTestResult && (
                        <div className={`p-3 rounded-sm border text-xs ${feedTestResult.error ? "bg-red-50 border-red-300 text-red-700" : (feedTestResult.missing_required?.length > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-green-50 border-green-300")}`} data-testid="feed-test-result">
                            {feedTestResult.error ? (
                                <div className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {feedTestResult.error}</div>
                            ) : feedTestResult.missing_required?.length > 0 ? (
                                <div>
                                    <div className="font-bold mb-1 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> Verbindung OK ({feedTestResult.total} Zeilen) — Pflichtspalten fehlen: {feedTestResult.missing_required.join(", ")}</div>
                                    <div className="text-amber-700">Bitte unten die passenden Quell-Spalten zuordnen und erneut testen.</div>
                                </div>
                            ) : (
                                <div>
                                    <div className="font-bold mb-1">Verbindung OK — {feedTestResult.total} Zeilen ({feedTestResult.with_external_id} mit externer ID, {feedTestResult.error_count} Fehler)</div>
                                    <div className="text-gray-600">Spalten: {feedTestResult.columns?.join(", ")}</div>
                                    {feedTestResult.errors?.length > 0 && (
                                        <div className="mt-1 text-red-700">{feedTestResult.errors.slice(0, 3).map((e, i) => <div key={i}>⚠ Z{e.row}: {e.error}</div>)}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Spalten-Mapping (erscheint, sobald der Test Spalten geliefert hat) */}
                    {feedTestResult && feedTestResult.columns && (
                        <div className="border border-gray-200 rounded-sm" data-testid="feed-column-map">
                            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center justify-between gap-2">
                                <span>Spalten-Zuordnung <span className="normal-case font-normal text-gray-400">(falls Ihr Export andere Spaltennamen nutzt)</span></span>
                                {feedTestResult.suggested_map && Object.keys(feedTestResult.suggested_map).length > 0 && (
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            setColumnMap((prev) => ({ ...prev, ...feedTestResult.suggested_map }));
                                            toast.success(`${Object.keys(feedTestResult.suggested_map).length} Spalten automatisch zugeordnet — bitte erneut testen`);
                                        }}
                                        className="h-7 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm text-[11px] normal-case"
                                        data-testid="feed-automap-btn"
                                    >
                                        ✨ Automatisch zuordnen
                                    </Button>
                                )}
                            </div>
                            <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-2 max-h-72 overflow-y-auto">
                                {(feedTestResult.mappable_fields || []).map((field) => {
                                    const required = (feedTestResult.required_fields || []).includes(field);
                                    const label = FEED_FIELD_LABELS[field] || field;
                                    return (
                                        <div key={field} className="flex items-center gap-2">
                                            <span className="text-xs w-28 shrink-0 truncate" title={field}>
                                                {label}{required && <span className="text-red-600 ml-0.5">*</span>}
                                            </span>
                                            <select
                                                value={columnMap[field] || ""}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setColumnMap((prev) => {
                                                        const next = { ...prev };
                                                        if (v) next[field] = v; else delete next[field];
                                                        return next;
                                                    });
                                                }}
                                                className="flex-1 min-w-0 border border-gray-300 rounded-sm h-8 px-1 text-xs bg-white"
                                                data-testid={`feed-map-${field}`}
                                            >
                                                <option value="">— gleicher Name ({field})</option>
                                                {feedTestResult.columns.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">
                                Nach Änderungen erneut „Verbindung testen" und „Speichern". Pflichtfelder sind mit <span className="text-red-600">*</span> markiert.
                            </div>
                        </div>
                    )}

                    {/* Letzter Lauf + Historie */}
                    {(lastRunSummary || feedRuns.length > 0) && (
                        <div className="border border-gray-200 rounded-sm" data-testid="feed-runs-history">
                            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                                <Clock className="w-3 h-3" /> Sync-Verlauf
                            </div>
                            <div className="divide-y divide-gray-100 max-h-52 overflow-y-auto">
                                {feedRuns.length === 0 ? (
                                    <div className="text-xs text-gray-400 text-center py-3">Noch keine Läufe.</div>
                                ) : feedRuns.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid="feed-run-row">
                                        <div className="flex items-center gap-2">
                                            <Badge className={`${r.status === "ok" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"} hover:bg-current text-[10px]`}>{r.status === "ok" ? "OK" : "Fehler"}</Badge>
                                            <span className="text-gray-500">{r.trigger === "auto" ? "automatisch" : "manuell"} · {r.source_type?.toUpperCase()}</span>
                                        </div>
                                        <div className="text-gray-700 font-mono-num">
                                            +{r.created} / ↻{r.updated}{r.deactivated ? ` / ⊘${r.deactivated}` : ""}{r.error_count ? ` / ⚠${r.error_count}` : ""}
                                        </div>
                                        <span className="text-gray-400">{(r.started_at || "").slice(0, 16).replace("T", " ")}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <SubHeader id="tokens" icon={Key} title="API-Tokens für Automatischen Fahrzeug-Import" hint="Persönliche Tokens für externe Inventur-Tools." />
            {open.tokens && (
                <div className="p-5" data-testid="vehicle-import-tokens-body">
                    <p className="text-xs text-gray-600 mb-3">
                        Persönliche Tokens für externe Tools (Inventur-Software, eigene Backends, automatisierte Inserats-Imports).
                        Token im Header <code className="bg-gray-100 px-1 rounded">X-API-Token</code> senden an{" "}
                        <code className="bg-gray-100 px-1 rounded font-mono-num text-[10px]">{apiBase}/external/listings</code>.
                    </p>
                    <div className="flex gap-2 mb-3">
                        <Input value={tokenName} onChange={(e) => setTokenName(e.target.value)} placeholder="Name (z.B. Lagerverwaltung)" className="rounded-sm" data-testid="new-token-name" />
                        <Button onClick={createToken} disabled={!tokenName.trim()} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="create-token-btn">Erstellen</Button>
                    </div>
                    {newToken && (
                        <div className="mb-3 bg-green-50 border border-green-300 rounded-sm p-3">
                            <div className="text-xs font-bold text-green-800 mb-1">Token erstellt — jetzt kopieren, danach nur noch der Preview sichtbar!</div>
                            <div className="font-mono-num text-sm bg-white border border-green-200 p-2 rounded-sm break-all" data-testid="new-token-value">{newToken.token}</div>
                            <Button onClick={() => { navigator.clipboard.writeText(newToken.token); toast.success("Kopiert"); }} className="mt-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm h-8">In Zwischenablage kopieren</Button>
                        </div>
                    )}
                    {tokens.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-4">Noch keine Tokens.</div>
                    ) : (
                        <div className="space-y-1">
                            {tokens.map((t) => (
                                <div key={t.id} className="flex items-center justify-between border border-gray-200 rounded-sm px-3 py-2 text-sm" data-testid="token-row">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold flex items-center gap-2">
                                            {t.name}
                                            {t.revoked && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">widerrufen</Badge>}
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-mono-num">{t.token_preview} · scope: {t.scope}</div>
                                    </div>
                                    {!t.revoked && (
                                        <button onClick={() => revokeToken(t.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-sm" data-testid="revoke-token-btn">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            </>)}
        </div>
    );
}
